import React, { useEffect, useState } from "react";
import {
  Button,
  ButtonToolbar,
  Card,
  Container,
  Tab,
  Tabs,
} from "react-bootstrap";
import UpdateFileList from "./update/UpdateFileList";
import LoadingDisplay from "../common/LoadingDisplay";
import ErrorDisplay from "../common/ErrorDisplay";
import { Problem } from "../api/backendApi";
import UpdateOptionsComponent from "./update/UpdateOptionsComponent";
import {
  releaseAPI,
  ReleaseModel,
  ReleaseVehicleFileModel,
} from "../api/releaseAPI";
import { DataFileModel, DataFileTypeModel } from "../api/vehicleFileAPI";
import { useRoutablePages } from "../common/RoutablePages";
import UpdateFileProcessing from "./update/UpdateFileProcessing";

export interface UpdateOptions {
  skipExistingMpfzFiles: boolean;
}

export interface UpdateInfo {
  vehicleFileNumber: string;
  include: boolean;
  allowSelect: boolean;
  includeModelInfo: boolean;
  isFireService: boolean;
  isRoadAssist: boolean;
  hasCrsFile: boolean;
  crsFileIsValidated: boolean;
  isNew: boolean;
  message: string;
}

function CreateUpdatePage() {
  const [error, setError] = useState<Problem>();
  const [loading, setLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [updateInfoList, setUpdateInfoList] = useState<UpdateInfo[]>();
  const [options, setOptions] = useState<UpdateOptions>({
    skipExistingMpfzFiles: false,
  });
  const [isBusy, setIsBusy] = useState(false);
  const [isValidated, setIsValidated] = useState(false);

  const includesCrsFile = (dataFiles: DataFileModel[]) => {
    for (let ii = 0; ii < dataFiles.length; ++ii) {
      const dataFile = dataFiles[ii];

      if (dataFile.type === DataFileTypeModel.CrsFile && !dataFile.deleted)
        return true;
    }

    return false;
  };

  const loadReleaseData = async () => {
    setLoading(true);
    const result = await releaseAPI.getUnreleasedFiles();
    setLoading(false);
    setIsValidated(false);

    if (result.ok) {
      setError(undefined);

      let updateInfoList: UpdateInfo[] = [];

      result.value.forEach((entry) => {
        let updateInfo: UpdateInfo = {
          vehicleFileNumber: entry.number,
          include: true,
          allowSelect: true,
          includeModelInfo: true,
          isFireService: entry.isNew,
          isRoadAssist: entry.isRsa,
          hasCrsFile: includesCrsFile(entry.dataFiles),
          crsFileIsValidated: entry.crsFileValidated,
          isNew: entry.isNew,
          message: "",
        };

        // validations
        if (entry.checkedOutBy) {
          updateInfo.message = `File is checked out by ${entry.checkedOutBy}.`;
        } else if (!entry.hasCrsFile) {
          updateInfo.message = "CRS file is missing.";
        } else if (!entry.hasAutoCadFile) {
          updateInfo.message = "AutoCAD file is missing.";
        } else if (!entry.hasModelInfo) {
          updateInfo.message = "Model info is missing.";
        } else if (!entry.helperFilesValidated) {
          updateInfo.message = "Helper files are not validated.";
        } else if (!entry.modelInfoValidated) {
          updateInfo.message = "Model info is not validated.";
        } else if (!entry.areComponentsSet) {
          updateInfo.message = "Components have not been set.";
        }

        // if there was an error, don't allow the file to be used
        if (updateInfo.message.length > 0) {
          updateInfo.allowSelect = false;
          updateInfo.includeModelInfo = false;
          updateInfo.include = false;
          updateInfo.isFireService = false;
          updateInfo.isRoadAssist = false;
        } else {
          if (!updateInfo.hasCrsFile) {
            if (
              entry.hasModelInfoChanges &&
              entry.dataFiles.length === 0 &&
              Object.keys(entry.otherDataFileChanges).length === 0
            )
              updateInfo.message = "Only model info changes will be included.";
            else
              updateInfo.message =
                "CRS file will not be part of the update package.";
          }
        }

        updateInfoList.push(updateInfo);
      });

      setUpdateInfoList(updateInfoList);
    } else {
      setError(result.problem);
    }
  };

  useEffect(() => {
    loadReleaseData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isStepAllowed = (step: number) => {
    switch (step) {
      case 0:
        return !isBusy && currentStep >= 0 && currentStep <= 1;
      case 1:
        return !isBusy && currentStep >= 0 && currentStep <= 2;
      case 2:
        return (
          !isBusy &&
          currentStep >= 1 &&
          currentStep <= 3 &&
          (updateInfoList?.filter((il) => il.include).length ?? 0) > 0
        );
      case 3:
        return !isBusy && isValidated && currentStep >= 2 && currentStep <= 3;
    }

    return false;
  };

  const tabContentStyle: React.CSSProperties = {
    height: "500px",
    overflow: "auto",
    borderColor: "#ddd",
    borderLeft: "1px solid #ddd",
    borderBottom: "1px solid #ddd",
    borderRight: "1px solid #ddd",
    padding: "16px",
  };

  const allowNext = () => isStepAllowed(currentStep + 1);
  const allowPrevious = () => isStepAllowed(currentStep - 1);

  const handleUpdateInfoListChanged = (updateInfoList: UpdateInfo[]) => {
    setUpdateInfoList(updateInfoList);
    setIsValidated(false);
  };

  const navigate = useRoutablePages();

  const handleCreateReleaseClick = async () => {
    if (!updateInfoList) return;

    const releaseModel = new ReleaseModel(
      updateInfoList
        .filter((ul) => ul.include)
        .map(
          (ul) =>
            new ReleaseVehicleFileModel(
              ul.vehicleFileNumber,
              ul.isFireService,
              ul.isRoadAssist,
            ),
        ),
    );
    setIsBusy(true);
    const result = await releaseAPI.createRelease(releaseModel);

    if (result.ok) {
      setError(undefined);
      window.alert(`Release '${result.value}' was created successfully!`);
      navigate.toReleaseHistory(result.value);
    } else {
      setError(result.problem);
    }
    setIsBusy(false);
  };

  return (
    <>
      <div className="main-container-title">
        <h1>Create new update</h1>
        <ErrorDisplay error={error} />
        <LoadingDisplay loading={loading} />
      </div>
      <div className="main-container-content">
        <Container>
          <Card>
            <Card.Body>
              <Tabs
                activeKey={currentStep}
                id="tabs"
                onSelect={(step) => setCurrentStep(Number(step))}
              >
                <Tab
                  disabled={!isStepAllowed(0)}
                  eventKey={0}
                  title="Options"
                  style={tabContentStyle}
                >
                  <UpdateOptionsComponent
                    options={options}
                    onOptionsChanged={(o) => setOptions(o)}
                  />
                </Tab>
                <Tab
                  disabled={!isStepAllowed(1)}
                  eventKey={1}
                  title="File list"
                  style={tabContentStyle}
                >
                  <UpdateFileList
                    updateInfoList={updateInfoList ?? []}
                    onChange={handleUpdateInfoListChanged}
                    onRequestReload={() => loadReleaseData()}
                  />
                </Tab>
                <Tab
                  disabled={!isStepAllowed(2)}
                  eventKey={2}
                  title="File processing"
                  style={tabContentStyle}
                >
                  <UpdateFileProcessing
                    updateOptions={options}
                    updateInfoList={updateInfoList}
                    onFinish={(success) => setIsValidated(success)}
                  />
                </Tab>
                <Tab
                  disabled={!isStepAllowed(3)}
                  eventKey={3}
                  title="Finish"
                  style={tabContentStyle}
                >
                  <Button
                    onClick={() => handleCreateReleaseClick()}
                    disabled={
                      isBusy ||
                      (updateInfoList?.filter((ui) => ui.include)?.length ??
                        0) <= 0
                    }
                  >
                    Create Release
                  </Button>
                </Tab>
              </Tabs>
            </Card.Body>
            <Card.Footer>
              <ButtonToolbar style={{ justifyContent: "flex-end" }}>
                <Button
                  disabled={!allowPrevious()}
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="ms-2"
                >
                  Previous
                </Button>
                <Button
                  disabled={!allowNext()}
                  onClick={() => setCurrentStep(currentStep + 1)}
                  className="ms-2"
                >
                  Next
                </Button>
              </ButtonToolbar>
            </Card.Footer>
          </Card>
        </Container>
      </div>
    </>
  );
}

export default CreateUpdatePage;
